/**
 * Core API Client Configuration for ArbiWin
 *
 * This module provides a centralized Axios client with comprehensive
 * configuration, interceptors, and error handling following the constitution
 * requirements for the ArbiWin platform.
 *
 * Features:
 * - Environment-based configuration
 * - Request/response logging
 * - Automatic retry logic
 * - CORS and credentials handling
 * - Error boundaries and recovery
 * - Performance monitoring
 */

import axios from 'axios';
import { getEnvironmentConfig } from '../../../config/environment';
import {
  createRequestInterceptor,
  createRequestErrorHandler
} from '../interceptors/RequestInterceptor';
import {
  createResponseInterceptor,
  createResponseErrorHandler
} from '../interceptors/ResponseInterceptor';
import { globalErrorHandler, errorUtils } from '../utils/ErrorHandler';
import { responseProcessor, responseUtils } from '../utils/ResponseUtils';
import { requestManager, requestUtils } from '../utils/RequestManager';

/**
 * API Client Configuration
 */
const API_CONFIG = {
  // Request timeout in milliseconds
  TIMEOUT: 30000, // 30 seconds

  // Retry configuration
  RETRY: {
    MAX_ATTEMPTS: 3,
    DELAY: 1000, // 1 second
    BACKOFF_FACTOR: 2 // Exponential backoff
  },

  // Request concurrency limits
  CONCURRENCY: {
    MAX_CONCURRENT: 10,
    QUEUE_SIZE: 100
  },

  // Cache configuration
  CACHE: {
    TTL: 5 * 60 * 1000, // 5 minutes
    MAX_ENTRIES: 100
  }
};

/**
 * Default headers for all requests
 */
const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma': 'no-cache',
  'Expires': '0'
};

/**
 * Request queue for managing concurrent requests
 */
class RequestQueue {
  constructor(maxConcurrent = API_CONFIG.CONCURRENCY.MAX_CONCURRENT) {
    this.maxConcurrent = maxConcurrent;
    this.running = new Set();
    this.queue = [];
  }

  async add(requestFunction) {
    return new Promise((resolve, reject) => {
      this.queue.push({ requestFunction, resolve, reject });
      this.process();
    });
  }

  async process() {
    if (this.running.size >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    const { requestFunction, resolve, reject } = this.queue.shift();
    const requestPromise = requestFunction();

    this.running.add(requestPromise);

    try {
      const result = await requestPromise;
      resolve(result);
    } catch (error) {
      reject(error);
    } finally {
      this.running.delete(requestPromise);
      this.process(); // Process next request
    }
  }
}

/**
 * Retry logic for failed requests
 */
const retryRequest = async (config, attempt = 1) => {
  const { MAX_ATTEMPTS, DELAY, BACKOFF_FACTOR } = API_CONFIG.RETRY;

  try {
    return await axios(config);
  } catch (error) {
    // Don't retry on 4xx client errors (except 429 Too Many Requests)
    if (error.response && error.response.status >= 400 && error.response.status < 500 && error.response.status !== 429) {
      throw error;
    }

    // Don't retry if we've exceeded max attempts
    if (attempt >= MAX_ATTEMPTS) {
      throw error;
    }

    // Calculate delay with exponential backoff
    const delay = DELAY * Math.pow(BACKOFF_FACTOR, attempt - 1);

    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 0.1 * delay;
    const totalDelay = delay + jitter;

    console.warn(`API request failed, retrying in ${totalDelay}ms (attempt ${attempt}/${MAX_ATTEMPTS})`, {
      url: config.url,
      method: config.method,
      error: error.message
    });

    await new Promise(resolve => setTimeout(resolve, totalDelay));

    return retryRequest(config, attempt + 1);
  }
};

/**
 * Performance monitoring for API requests
 */
const performanceMonitor = {
  requests: new Map(),

  startTimer: function(requestId) {
    this.requests.set(requestId, {
      startTime: performance.now(),
      endTime: null,
      duration: null
    });
  },

  endTimer: function(requestId) {
    const request = this.requests.get(requestId);
    if (request) {
      request.endTime = performance.now();
      request.duration = request.endTime - request.startTime;

      // Log slow requests in development
      if (request.duration > 3000) { // 3 seconds
        console.warn(`Slow API request detected: ${requestId} took ${request.duration.toFixed(2)}ms`);
      }

      // Clean up old entries
      setTimeout(() => {
        this.requests.delete(requestId);
      }, 60000); // Clean up after 1 minute
    }
  },

  getStats: function() {
    const durations = Array.from(this.requests.values())
      .filter(req => req.duration !== null)
      .map(req => req.duration);

    if (durations.length === 0) return null;

    return {
      count: durations.length,
      average: durations.reduce((a, b) => a + b, 0) / durations.length,
      min: Math.min(...durations),
      max: Math.max(...durations)
    };
  }
};

/**
 * Create and configure the main API client
 * @returns {Object} Configured Axios instance
 */
export function createApiClient() {
  const envConfig = getEnvironmentConfig();
  const requestQueue = new RequestQueue();

  // Create base Axios instance
  const client = axios.create({
    baseURL: envConfig.api.baseURL,
    timeout: API_CONFIG.TIMEOUT,
    // Don't set default headers here - let the interceptor handle them conditionally
    headers: {},

    // CORS and credentials configuration
    withCredentials: envConfig.api.withCredentials || false,

    // Validate status codes
    validateStatus: function(status) {
      // Accept all status codes, handle errors in interceptors
      return status >= 200 && status < 600;
    }
  });

  // Add request queue wrapper
  const originalRequest = client.request;
  client.request = function(config) {
    return requestQueue.add(() => originalRequest.call(this, config));
  };

  // Request interceptor with request management integration
  client.interceptors.request.use(
    (config) => {
      // Add request metadata for management
      config.metadata = config.metadata || {};
      config.metadata.requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      config.metadata.timestamp = Date.now();

      // Process through request interceptor
      return createRequestInterceptor()(config);
    },
    createRequestErrorHandler()
  );

  // Response interceptor with integrated response processing
  client.interceptors.response.use(
    async (response) => {
      // First, let the response interceptor process
      const processedResponse = createResponseInterceptor()(response);

      // Then process through response utilities if enabled
      if (response.config?.processResponse !== false) {
        try {
          const requestId = response.config?.metadata?.requestId;
          const processingOptions = response.config?.responseProcessing || {};

          const processed = await responseProcessor.processResponse(response, {
            requestId,
            ...processingOptions
          });

          // Add processed response data to the response
          response.processedData = processed;
        } catch (processingError) {
          console.warn('Response processing error:', processingError);
        }
      }

      return processedResponse;
    },
    (error) => {
      console.log('🚨 ApiClient: Error interceptor triggered:', {
        status: error.response?.status,
        url: error.config?.url,
        hasResponse: !!error.response,
        hasConfig: !!error.config,
        errorMessage: error.message
      });
      
      // First, let the response error handler process the error
      const responseErrorPromise = createResponseErrorHandler()(error);

      // Process through global error handler
      const processedError = globalErrorHandler.handleError(error, {
        url: error.config?.url,
        method: error.config?.method,
        timestamp: new Date().toISOString(),
        requestId: error.config?.metadata?.requestId
      });

      // Add processed error information
      error.processedError = processedError;

      // Return the response error handler result
      return responseErrorPromise;
    }
  );

  // Add retry functionality
  client.interceptors.response.use(
    response => response,
    async error => {
      // Only retry if the original request doesn't have a retry flag
      if (!error.config._retry && shouldRetry(error)) {
        error.config._retry = true;
        return retryRequest(error.config);
      }

      throw error;
    }
  );

  return client;
}

/**
 * Determine if a request should be retried
 * @param {Object} error - The error object
 * @returns {boolean} Whether to retry the request
 */
function shouldRetry(error) {
  // Don't retry if no response (likely network error)
  if (!error.response) return true;

  const status = error.response.status;

  // Retry on server errors and rate limiting
  return status >= 500 || status === 429;
}

/**
 * Create API client instance with error boundaries
 * @returns {Object} API client with error handling
 */
export function createSecureApiClient() {
  const client = createApiClient();

  // Wrap all methods with error boundaries
  const secureClient = {
    get: async (...args) => {
      try {
        return await client.get(...args);
      } catch (error) {
        throw enhanceError(error, 'GET');
      }
    },

    post: async (...args) => {
      try {
        return await client.post(...args);
      } catch (error) {
        throw enhanceError(error, 'POST');
      }
    },

    put: async (...args) => {
      try {
        return await client.put(...args);
      } catch (error) {
        throw enhanceError(error, 'PUT');
      }
    },

    delete: async (...args) => {
      try {
        return await client.delete(...args);
      } catch (error) {
        throw enhanceError(error, 'DELETE');
      }
    },

    patch: async (...args) => {
      try {
        return await client.patch(...args);
      } catch (error) {
        throw enhanceError(error, 'PATCH');
      }
    },

    request: async (...args) => {
      try {
        return await client.request(...args);
      } catch (error) {
        throw enhanceError(error, 'REQUEST');
      }
    },

    // Expose original client for advanced usage
    _client: client,

    // Performance monitoring
    getPerformanceStats: () => performanceMonitor.getStats(),

    // Health check
    healthCheck: async () => {
      try {
        const start = performance.now();
        await client.get('/health');
        const duration = performance.now() - start;
        return { healthy: true, responseTime: duration };
      } catch (error) {
        return { healthy: false, error: error.message };
      }
    },

    // Error handling utilities
    errorHandler: {
      getStats: () => globalErrorHandler.getErrorStats(),
      clearStats: () => globalErrorHandler.clearErrorStats(),
      setLanguage: (language) => globalErrorHandler.setLanguage(language),
      onError: (listener) => globalErrorHandler.onError(listener),
      onRecovery: (listener) => globalErrorHandler.onRecovery(listener)
    },

    // Retry utilities
    withRetry: (requestFn, context = {}) => {
      return errorUtils.retry(requestFn, context);
    },

    // Response processing utilities
    responseProcessor: {
      getMetrics: () => responseProcessor.getPerformanceMetrics(),
      getCacheStats: () => responseProcessor.getCacheStats(),
      clearCache: () => responseProcessor.clearCache(),
      resetMetrics: () => responseProcessor.resetMetrics()
    },

    // Response utilities
    processResponse: (response, options = {}) => {
      return responseUtils.process(response, options);
    },

    getCachedResponse: (url, params = {}) => {
      return responseUtils.getCached(url, params);
    },

    validateResponse: (data, schema) => {
      return responseUtils.validate(data, schema);
    },

    transformResponse: (data, transformer) => {
      return responseUtils.transform(data, transformer);
    },

    generateMockData: (type, count) => {
      return responseUtils.generateMock(type, count);
    },

    // Request management utilities
    requestManager: {
      getStats: () => requestManager.getStats(),
      resetStats: () => requestManager.resetStats(),
      cancelRequest: (requestId, reason) => requestManager.cancelRequest(requestId, reason),
      cancelAllRequests: (reason) => requestManager.cancelAllRequests(reason)
    },

    // Managed request execution
    executeRequest: (requestFn, options = {}) => {
      return requestUtils.execute(requestFn, options);
    },

    // Managed API calls with full request management
    managedGet: (url, config = {}) => {
      return requestUtils.execute(
        ({ signal, requestId }) => client.get(url, {
          ...config,
          signal,
          metadata: { ...config.metadata, requestId }
        }),
        { ...config, url, method: 'GET' }
      );
    },

    managedPost: (url, data, config = {}) => {
      return requestUtils.execute(
        ({ signal, requestId }) => client.post(url, data, {
          ...config,
          signal,
          metadata: { ...config.metadata, requestId }
        }),
        { ...config, url, method: 'POST', data }
      );
    },

    managedPut: (url, data, config = {}) => {
      return requestUtils.execute(
        ({ signal, requestId }) => client.put(url, data, {
          ...config,
          signal,
          metadata: { ...config.metadata, requestId }
        }),
        { ...config, url, method: 'PUT', data }
      );
    },

    managedDelete: (url, config = {}) => {
      return requestUtils.execute(
        ({ signal, requestId }) => client.delete(url, {
          ...config,
          signal,
          metadata: { ...config.metadata, requestId }
        }),
        { ...config, url, method: 'DELETE' }
      );
    }
  };

  return secureClient;
}

/**
 * Enhance error with additional context
 * @param {Object} error - Original error
 * @param {string} method - HTTP method
 * @returns {Object} Enhanced error
 */
function enhanceError(error, method) {
  return {
    ...error,
    method: method,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href
  };
}

/**
 * Default API client instance
 */
export const apiClient = createSecureApiClient();

/**
 * Export configuration for testing and debugging
 */
export { API_CONFIG, DEFAULT_HEADERS, performanceMonitor };

export default apiClient;
